// Función para resetear todos los datos de la aplicación
import { db } from "@/firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { hardReset } from "./hardReset";
import { wipeNestedDaysCollections } from "./deepCleanup";

/**
 * Delay helper para evitar saturar la API
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Elimina documentos en lotes para evitar exceder cuota de Firebase
 */
async function deleteDocsInBatches(docs, batchSize = 5, delayMs = 500) {
  let deleted = 0;
  
  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = docs.slice(i, i + batchSize);
    
    try {
      await Promise.all(
        batch.map(doc => deleteDoc(doc.ref))
      );
      deleted += batch.length;
      console.log(`  ✓ ${deleted}/${docs.length} eliminados`);
      
      // Esperar entre lotes para no saturar
      if (i + batchSize < docs.length) {
        await delay(delayMs);
      }
    } catch (error) {
      if (error.code === 'resource-exhausted') {
        console.warn(`⚠️  Cuota excedida. Esperando 2 segundos...`);
        await delay(2000);
        
        // Reintentar lote actual
        i -= batchSize;
        continue;
      }
      throw error;
    }
  }
  
  return deleted;
}

/**
 * Asegura que hay autenticación antes de acceder a Firestore
 */
async function ensureAuth() {
  const auth = getAuth();
  // Ya autenticado
  if (auth.currentUser) {
    console.log("[RESET] auth ok:", auth.currentUser.uid);
    return auth.currentUser;
  }
  // Esperar a onAuthStateChanged o iniciar anon
  console.log("[RESET] auth missing → signing in anonymously…");
  return new Promise((resolve, reject) => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        console.log("[RESET] auth ok:", u.uid);
        unsub();
        resolve(u);
      }
    });
    signInAnonymously(auth).catch((e) => {
      console.error("[RESET] anon sign-in fail:", e.message);
      reject(e);
    });
  });
}

/**
 * Descubre todos los workspaces disponibles dinámicamente
 */
async function discoverWorkspaces() {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;
    
    if (!currentUserId) {
      console.warn("[RESET] No hay usuario autenticado para descubrir workspaces");
      return [];
    }

    console.log(`[RESET] Usando UID real: ${currentUserId}`);
    return [currentUserId];
  } catch (error) {
    console.error(`[RESET] Error descubriendo workspaces:`, error.message);
    return [];
  }
}

/**
 * Elimina todas las subcolecciones conocidas de un documento de día
 */
async function deleteKnownSubcollections(dayDocRef, dayKey) {
  const knownSubcollections = ['dogs', 'states'];
  let totalSubDocsDeleted = 0;
  
  for (const subCollName of knownSubcollections) {
    try {
      const subCollRef = collection(dayDocRef, subCollName);
      const subSnapshot = await getDocs(subCollRef);
      
      if (!subSnapshot.empty) {
        console.log(`[RESET_DBG] deleting ${subCollName} subcollection count: ${subSnapshot.size}`);
        
        for (const subDoc of subSnapshot.docs) {
          await deleteDoc(subDoc.ref);
          totalSubDocsDeleted++;
        }
      }
    } catch (subError) {
      // La subcolección puede no existir, lo cual es normal
      if (subError.code !== 'permission-denied') {
        console.log(`[RESET_DBG] subcollection '${subCollName}' not found or empty (normal)`);
      }
    }
  }
  
  return totalSubDocsDeleted;
}

/**
 * Elimina todos los documentos de días de Firestore
 * Borra SOLO los documentos del usuario autenticado
 */
export async function resetFirestoreData() {
  console.log("🧹 Limpiando datos de Firestore...");
  
  try {
    // Asegurar autenticación antes de acceder a Firestore
    const user = await ensureAuth();
    const currentUserId = user.uid;
    
    console.log(`[RESET_DBG] Current UID: ${currentUserId}`);
    console.log(`[RESET] Limpiando workspace: ${currentUserId}`);
    
    const daysRef = collection(db, "workspaces", currentUserId, "days");
    const snapshot = await getDocs(daysRef);
    
    if (snapshot.empty) {
      console.log(`[RESET] 0 días encontrados (workspace vacío)`);
      console.log(`[RESET_RESULT] Workspace already clean - 0 days to delete`);
      return { success: true, deleted: 0, profiles: 1 };
    }
    
    const dayIds = snapshot.docs.map(d => d.id);
    console.log(`[RESET_DBG] Days found (${snapshot.size}): ${dayIds.join(', ')}`);
    console.log(`[RESET] Encontrados ${snapshot.size} días para eliminar`);
    
    let totalDeleted = 0;
    let totalSubDocsDeleted = 0;
    const failedDayIds = [];
    
    // Para cada día: borrar subcolecciones primero, luego el documento
    for (const docSnapshot of snapshot.docs) {
      const dayKey = docSnapshot.id;
      const docPath = `workspaces/${currentUserId}/days/${dayKey}`;
      
      try {
        // Paso 1: Log que se encontró el día
        console.log(`[RESET_DBG] day found: ${dayKey}`);
        
        // Paso 2: Eliminar todas las subcolecciones conocidas
        const subDocsDeleted = await deleteKnownSubcollections(docSnapshot.ref, dayKey);
        totalSubDocsDeleted += subDocsDeleted;
        
        // Paso 3: Eliminar el documento del día
        await deleteDoc(docSnapshot.ref);
        totalDeleted++;
        console.log(`[RESET_DBG] deleted day: ${dayKey}`);
        
        // Delay entre eliminaciones para no saturar
        if (totalDeleted % 5 === 0) {
          await delay(200);
        }
      } catch (docError) {
        console.error(`[RESET] ✗ FAIL eliminando ${docPath}:`, docError.message);
        failedDayIds.push(dayKey);
      }
    }
    
    console.log(`[RESET] Eliminados ${totalDeleted} días y ${totalSubDocsDeleted} subdocumentos ✅`);
    
    // Verificación final: consultar de nuevo el workspace - SIEMPRE mostrar resultado
    console.log(`[RESET_DBG] Verificando workspace final...`);
    const finalSnapshot = await getDocs(daysRef);
    const remainingCount = finalSnapshot.size;
    const remainingIds = finalSnapshot.docs.map(d => d.id);
    
    console.log(`[RESET_VERIFY] remaining days: ${remainingCount}, IDs: ${remainingIds.length > 0 ? remainingIds.join(', ') : 'NONE'}`);
    
    if (remainingCount === 0) {
      console.log(`[RESET_COMPLETE] all days and subcollections fully cleared`);
    } else {
      console.warn(`[RESET] ⚠️ WARNING: ${remainingCount} days still exist after reset`);
    }
    
    if (failedDayIds.length > 0) {
      console.warn(`[RESET_DBG] Failed to delete ${failedDayIds.length} days: ${failedDayIds.join(', ')}`);
    }
    
    return { success: finalSnapshot.empty, deleted: totalDeleted, profiles: 1 };
  } catch (error) {
    console.error("[RESET] ❌ Error limpiando Firestore:", error);
    console.error(`[RESET_RESULT] ERROR - ${error.message}`);
    return { success: false, error: error.message };
  }
}

/**
 * Limpia todos los datos de localStorage y sessionStorage relacionados con la app
 */
export function resetLocalStorage() {
  console.log("🧹 Limpiando localStorage...");
  
  try {
    // Obtener todas las claves del localStorage
    const keys = Object.keys(localStorage);
    let cleaned = 0;
    
    // Limpiar claves relacionadas con la app (excepto auth si existe)
    keys.forEach(key => {
      // No eliminar datos de autenticación de Firebase
      if (!key.startsWith('firebase:')) {
        localStorage.removeItem(key);
        cleaned++;
        console.log(`  ✓ Eliminado: ${key}`);
      }
    });
    
    console.log(`✅ ${cleaned} entradas eliminadas de localStorage`);
    return { success: true, cleaned };
  } catch (error) {
    console.error("❌ Error limpiando localStorage:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Limpia todos los datos de sessionStorage (selección de perfil, etc)
 */
export function resetSessionStorage() {
  console.log("🧹 Limpiando sessionStorage...");
  
  try {
    const keys = Object.keys(sessionStorage);
    let cleaned = 0;
    
    keys.forEach(key => {
      sessionStorage.removeItem(key);
      cleaned++;
      console.log(`  ✓ Eliminado: ${key}`);
    });
    
    console.log(`✅ ${cleaned} entradas eliminadas de sessionStorage`);
    return { success: true, cleaned };
  } catch (error) {
    console.error("❌ Error limpiando sessionStorage:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Desconecta Firestore ANTES de resetear
 * Busca la función de desconexión registrada por AppCore
 */
function disconnectFirestoreBeforeReset() {
  try {
    // La función se registra globalmente por AppCore.jsx
    if (typeof window.getDisconnectFirestore === "function") {
      const disconnect = window.getDisconnectFirestore();
      if (typeof disconnect === "function") {
        disconnect();
        console.log("[RESET] ✅ Firestore desconectado antes del reset");
        return true;
      }
    }
  } catch (error) {
    console.warn("[RESET] ⚠️  No se pudo desconectar Firestore:", error.message);
  }
  return false;
}

/**
 * Resetea completamente la aplicación
 * Primero desconecta Firestore, luego elimina todos los datos
 */
export async function resetApp() {
  console.log("\n🔄 INICIANDO RESETEO COMPLETO DE LA APP\n");
  
  // PASO 0: Desconectar Firestore PRIMERO
  console.log("[RESET] Desconectando Firestore...");
  disconnectFirestoreBeforeReset();
  
  // Esperar un poco para asegurar que las suscripciones se cierren
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const results = {
    firestore: await resetFirestoreData(),
    localStorage: resetLocalStorage(),
    sessionStorage: resetSessionStorage()
  };
  
  console.log("\n📊 RESUMEN:");
  console.log(`  Firestore: ${results.firestore.deleted || 0} documentos eliminados`);
  console.log(`  localStorage: ${results.localStorage.cleaned || 0} entradas eliminadas`);
  console.log(`  sessionStorage: ${results.sessionStorage.cleaned || 0} entradas eliminadas`);
  
  if (results.firestore.success && results.localStorage.success && results.sessionStorage.success) {
    console.log("\n✅ ¡RESETEO DE DATOS COMPLETADO CON ÉXITO!");
  } else {
    console.log("\n⚠️  El reseteo de datos se completó con algunos errores");
  }
  
  console.log("\n🔄 Iniciando limpieza completa con hardReset...\n");
  
  // Usar hardReset para limpieza completa y reload
  await hardReset();
  
  return results;
}
